import { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import { H4 } from '../../base';
import { colors, fonts } from '../../styles/theme';
import customIcons from '../../styles/icons';

const collapsibleIndent = '20px';

const CollapsibleContent = (props) => props.children;

CollapsibleContent.propTypes = {
  children: PropTypes.node,
};

const GenericCollapsibleHeader = styled('div')`
  font-family: ${fonts.sans.family};
  padding-left: 25px;
  position: relative;
  &::before {
    ${fonts.icons}
    color: ${colors.blue.primary};
    content: '${customIcons['angle-down']}';
    display: block;
    font-size: 20px;
    left: 7px;
    line-height: 1;
    position: absolute;
    text-align: center;
    top: 12px;
    width: 20px;
  }
  &:hover {
    cursor: pointer;
  }
`;

const DefaultCollapsibleHeader = styled(H4)`
  font-family: ${fonts.sans.family};
  font-size: ${fonts.sans.size.large};
  font-weight: ${fonts.sans.weight.bold};
  padding-left: ${collapsibleIndent};
  position: relative;
  &::before {
    ${fonts.icons}
    color: ${colors.blue.primary};
    content: '${customIcons['angle-down']}';
    display: block;
    font-size: 20px;
    left: 0;
    line-height: 1;
    position: absolute;
    text-align: center;
    top: -1px;
    width: 20px;
  }
  &:hover {
    cursor: pointer;
  }
`;

const CollapsibleContentWrapper = styled('div')`
  margin: 0 ${collapsibleIndent};
  text-align: left;
  > div > h6:first-of-type {
    padding-top: 3px;
  }
`;

const CollapsibleHeader = (props) => {
  const { handleClick, header, collapsed, hasCollapsedText } = props;

  if (typeof header === 'string')
    return (
      <DefaultCollapsibleHeader
        id="collapsibleHeader"
        className="collapsibleHeader"
        collapsed={collapsed}
        hasCollapsedText={hasCollapsedText}
        onClick={handleClick}
      >
        {header}
      </DefaultCollapsibleHeader>
    );
  else
    return (
      <GenericCollapsibleHeader
        collapsed={collapsed}
        hasCollapsedText={hasCollapsedText}
        onClick={handleClick}
      >
        {header}
      </GenericCollapsibleHeader>
    );
};

CollapsibleHeader.defaultProps = {
  hasCollapsedText: false,
  collapsed: null,
};

CollapsibleHeader.propTypes = {
  handleClick: PropTypes.func.isRequired,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  collapsed: PropTypes.bool,
  hasCollapsedText: PropTypes.bool,
};

const collapsedHeaderStyles = css`
  margin-bottom: 0;
  &::before {
    content: '${customIcons['angle-right']}';
    color: ${colors.grey.primaryLight};
  }
`;

const collapsedContentStyles = css`
  font-size: ${fonts.sans.size.small};
  color: ${colors.grey.primary};
`;

const CollapsibleContainer = styled('div')`
  ${DefaultCollapsibleHeader} {
    ${(props) => (props.hasCollapsedText && props.collapsed ? collapsedHeaderStyles : '')};
  }
  ${GenericCollapsibleHeader} {
    ${(props) => (props.collapsed ? collapsedHeaderStyles : '')};
  }
  ${CollapsibleContentWrapper} {
    ${(props) => (props.hasCollapsedText && props.collapsed ? collapsedContentStyles : '')};
  }
`;

export default class Collapsible extends Component {
  constructor(props) {
    super(props);
    this.state = {
      collapsed: props.collapsed || false,
    };
  }

  toggleCollapsible = () => {
    const { onChange } = this.props;

    this.setState(
      (state) => {
        return { collapsed: !state.collapsed };
      },
      () => {
        if (onChange) {
          onChange(this.state.collapsed);
        }
      }
    );
  };

  render() {
    const { collapsed } = this.state;
    const { header, collapsedText, children, wrapperClassName } = this.props;

    const hasCollapsedText = collapsedText !== '';

    return (
      <CollapsibleContainer
        className={wrapperClassName}
        collapsed={collapsed}
        hasCollapsedText={hasCollapsedText}
      >
        <CollapsibleHeader header={header} handleClick={this.toggleCollapsible} />
        <CollapsibleContentWrapper>
          <CollapsibleContent>{collapsed ? collapsedText : children}</CollapsibleContent>
        </CollapsibleContentWrapper>
      </CollapsibleContainer>
    );
  }
}

Collapsible.defaultProps = {
  collapsedText: null,
  collapsed: false,
  onChange: () => {},
  wrapperClassName: '',
};

Collapsible.propTypes = {
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  children: PropTypes.node,
  collapsed: PropTypes.bool,
  collapsedText: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  onChange: PropTypes.func,
  wrapperClassName: PropTypes.string,
};
